import { C, soft } from '@parsina/framework/clinical/src/constants/theme';
import { CT } from '@parsina/framework/tokens';
import { Play, Clock } from 'lucide-react';
import { buildHeroModel, formatDuration } from '../lib/appointments';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import PatientPhones from './PatientPhones';

// The DESKTOP next-patient hero. The mobile surface renders its own
// `MobileNextPatientCard` (src/components/mobile/) — both consume the same
// `buildHeroModel` (lib/appointments), so they escalate identically: quiet within
// the slot length (brand sweep), amber past it, red past twice it, with «پایان
// ویزیت» promoted once closing is as likely the right action as continuing.

// One overrun chip vocabulary for the hero, tokenised.
const HERO_CHIP = {
  normal:   { bg: 'rgba(255,255,255,0.15)', color: 'white' },
  over:     { bg: C.amberLight, color: CT.warnInk },
  critical: { bg: C.redLight,  color: CT.dangerInk },
};

// ── Wide (web) ────────────────────────────────────────────────────────────────
const HeroWide = ({ apt, m, onStart, onHistory, onEnd }) => {
  const { t } = useTranslation();
  const chip = HERO_CHIP[m.level];
  return (
    <div style={{
      background: m.background, borderRadius: 18, padding: '22px 24px', marginBottom: 20,
      boxShadow: m.level === 'critical' ? '0 8px 24px rgba(153,27,27,0.3)' : `0 8px 24px ${soft(C.navy, 0.22)}`,
      border: m.level === 'over' ? `2px solid ${C.amber}` : 'none',
      position: 'relative', overflow: 'hidden',
    }}>
      <div style={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
      <div style={{ position: 'absolute', bottom: -20, right: 80, width: 80, height: 80, borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ width: 7, height: 7, borderRadius: '50%', background: m.dotColor, boxShadow: `0 0 0 3px ${m.dotColor}4D` }} />
        <span style={{ fontSize: '0.6875rem', fontWeight: 700, color: 'rgba(255,255,255,0.7)', letterSpacing: '0.05em' }}>{m.heroLabel}</span>
        {m.urg && (
          <span style={{ fontSize: '0.625rem', fontWeight: 800, borderRadius: 6, padding: '2px 8px', background: m.urg.bg, color: m.urg.color }}>
            {t(m.urg.labelKey)}
          </span>
        )}
        {m.inVisit && m.elapsedMin != null && (
          <span style={{ fontSize: '0.6875rem', fontWeight: 800, borderRadius: 20, padding: '3px 10px', background: chip.bg, color: chip.color, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <Clock size={12} strokeWidth={2.4} />{' '}
            {t('dashboard.hero.sinceStart', { duration: formatDuration(t, m.elapsedMin) })}
            {m.level === 'over' && t('dashboard.hero.overSlot')}
            {m.level === 'critical'
              && t('dashboard.hero.usualLength', {
                duration: formatDuration(t, apt.durationMinutes || 30),
              })}
          </span>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{
            width: 54, height: 54, borderRadius: '50%', background: 'rgba(255,255,255,0.2)',
            border: '2px solid rgba(255,255,255,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '1.125rem', fontWeight: 800, color: 'white', flexShrink: 0,
          }}>{m.initials}</div>
          <div>
            <div style={{ fontSize: '1.125rem', fontWeight: 800, color: 'white', lineHeight: 1.2 }}>{apt.name || t('dashboard.hero.unknownPatient')}</div>
            {(apt.patient?.phone || apt.specialty) && (
              <div style={{ fontSize: '0.78125rem', color: 'rgba(255,255,255,0.65)', marginTop: 5 }}>
                {/* The kit's read path never blanks, so an unparseable stored number
                    stays on screen instead of falling through to the specialty, which
                    would be a different fact silently. The ternary still gates on the
                    raw column, not on the rendering. */}
                {apt.patient?.phone
                  ? (
                    // The badge keeps the hero's own ink: the outline badge's dark
                    // text would vanish on the gradient.
                    <PatientPhones
                      patient={apt.patient}
                      badgeStyle={{ color: 'inherit', borderColor: 'currentColor' }}
                      data-testid="next-patient-phones"
                    />
                  )
                  : apt.specialty}
              </div>
            )}
          </div>
        </div>
        <div style={{ textAlign: 'end', flexShrink: 0 }}>
          <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: 10, padding: '6px 14px', fontSize: '1.25rem', fontWeight: 800, color: 'white', textAlign: 'center' }}>{apt.time}</div>
          <div style={{ fontSize: '0.6875rem', color: 'rgba(255,255,255,0.6)', marginTop: 5, textAlign: 'center' }}>{m.waitLabel}</div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, marginTop: 18, justifyContent: 'flex-end' }}>
        <button onClick={onHistory} style={{
          height: 36, padding: '0 16px', background: 'rgba(255,255,255,0.14)', color: 'white',
          border: '1px solid rgba(255,255,255,0.22)', borderRadius: 10, fontSize: '0.78125rem', fontWeight: 700, cursor: 'pointer',
        }}>{t('dashboard.hero.history')}</button>
        {m.inVisit && (
          <button onClick={onEnd} style={{
            height: 36, padding: '0 18px',
            background: m.level === 'normal' ? 'rgba(255,255,255,0.14)' : 'white',
            color: m.level === 'normal' ? 'white' : CT.dangerInk,
            border: m.level === 'normal' ? '1px solid rgba(255,255,255,0.22)' : 'none',
            borderRadius: 10, fontSize: '0.8125rem', fontWeight: 800, cursor: 'pointer',
            boxShadow: m.level === 'normal' ? 'none' : '0 2px 8px rgba(0,0,0,0.12)',
          }}>{t('dashboard.hero.endVisit')}</button>
        )}
        <button onClick={onStart} style={{
          height: 36, padding: '0 20px', background: 'white',
          color: m.level === 'critical' ? CT.dangerInk : C.navy,
          border: 'none', borderRadius: 10, fontSize: '0.8125rem', fontWeight: 800, cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(0,0,0,0.12)', display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <Play size={14} strokeWidth={2.6} fill="currentColor" /> {m.inVisit ? t('dashboard.hero.continueVisit') : t('dashboard.hero.startVisit')}
        </button>
      </div>
    </div>
  );
};

const NextPatientHero = ({ apt, nowMin, nowMs, onStart, onHistory, onEnd }) => {
  const { t } = useTranslation();
  const m = buildHeroModel(apt, nowMin, nowMs, t);
  return <HeroWide apt={apt} m={m} onStart={onStart} onHistory={onHistory} onEnd={onEnd} />;
};

export default NextPatientHero;
